/* ------------------------ Functional imports start ------------------------ */
import { type GuestRequest, type GuestRequestFilesReference, type GuestRequestArticlesReference, type GuestRequestEditorsReference, type GuestRequestEditor } from './types'
import { type APIGuestRequest, APIGuestRequestFromJSON } from '../../../generated-types/models/APIGuestRequest'
import LogTool from '../../../logger/logTools'
import { apiClient, type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { getPKfromSelf, normalizeEditors, parseNestedObjectList } from '../../../utils/functions'
import { type SelectOption, type FileInfo } from '../../../utils/types'
import { createArticle } from '../../Item/utils/functions'
import { Property, type Article, Part } from '../../Item/utils/types'
import {
  fetchGuestRequests as fetchGuestRequestsApi,
  deprecatedFetchGuestRequests as deprecatedFetchGuestRequestsApi,
  fetchGuestRequest as fetchGuestRequestApi,
  deleteGuestRequestsApi,
} from '../api/guestRequestApi'
import {
  type CompleteRequestData,
  type CompleteRequestFormStateInput,
  type PartInfoFormStateInput,
  type ProductInfoFormStateInput,
  type Request,
} from '../../Request/utils/types'
import { APIPart } from '../../../generated-types'
/* ------------------------- Functional imports end ------------------------- */

const log = new LogTool({ context: 'UnauthRequestFunctions', enable: true, logLevel: 'warn' })

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */

export function createGuestRequest(guestRequestResponse: unknown): GuestRequest {
  const apiGuestRequest: APIGuestRequest = APIGuestRequestFromJSON(guestRequestResponse)
  log.debug('GuestRequestResponse ->', guestRequestResponse)

  if (!apiGuestRequest) {
    throw new Error('Failed to parse guest request response')
  }

  return {
    ...apiGuestRequest,
    key: getPKfromSelf(apiGuestRequest.self),
    read: resolveGuestRequestRead(guestRequestResponse),
    createdAt: apiGuestRequest.createdAt,
    files: parseGuestRequestFiles(apiGuestRequest.files, guestRequestResponse),
    articles: parseGuestRequestArticles((guestRequestResponse as any).articles),
    editors: parseGuestRequestEditors(apiGuestRequest.editors, guestRequestResponse),
  }
}

function resolveGuestRequestRead(guestRequestResponse: unknown): boolean {
  if (
    typeof guestRequestResponse === 'object' &&
    guestRequestResponse !== null &&
    'read' in guestRequestResponse
  ) {
    return Boolean((guestRequestResponse as { read: unknown }).read)
  }
  return false
}

function parseGuestRequestFiles(
  filesField: string,
  guestRequestResponse: unknown
): GuestRequestFilesReference {
  const parsed = parseNestedObjectList(filesField, raw => enrichFile(raw))
  if (Array.isArray(parsed)) {
    return parsed
  }

  if (
    typeof guestRequestResponse === 'object' &&
    guestRequestResponse !== null &&
    'files' in guestRequestResponse
  ) {
    const files = (guestRequestResponse as { files: unknown }).files
    if (Array.isArray(files)) {
      return files.map(enrichFile)
    }
    return files as GuestRequestFilesReference
  }

  return filesField
}

function enrichFile(file: unknown): FileInfo {
  if (
    typeof file === 'object' &&
    file !== null &&
    'self' in file
  ) {
    const typedFile = file as { self: string; created_at?: string; createdAt?: string }
    const createdAt = typedFile.createdAt ?? typedFile.created_at ?? undefined
    return {
      ...(file as Record<string, unknown>),
      key: typedFile.self,
      createdAt: createdAt ? new Date(createdAt) : undefined,
    }
  }

  return file as FileInfo
}

function parseGuestRequestArticles(articlesField: string): GuestRequestArticlesReference {
  const parsed = parseNestedObjectList(articlesField, createArticle)
  if (Array.isArray(parsed)) {
    return parsed
  }
  return articlesField
}

function parseGuestRequestEditors(
  editorsField: string[] | string | undefined,
  guestRequestResponse: unknown
): GuestRequestEditorsReference | undefined {
  if (typeof editorsField === 'undefined') return undefined

  const parsedEditors: string | GuestRequestEditor[] = parseNestedObjectList(editorsField, parseEditor)
  if (Array.isArray(parsedEditors)) {
    return parsedEditors
  }

  if (
    typeof guestRequestResponse === 'object' &&
    guestRequestResponse !== null &&
    'editors' in guestRequestResponse
  ) {
    return (guestRequestResponse as { editors: GuestRequestEditorsReference }).editors
  }

  return;
}

function parseEditor(editor: unknown): GuestRequestEditor {
  if (
    typeof editor === 'object' &&
    editor !== null &&
    'self' in editor
  ) {
    const { self, first_name, last_name } = editor as {
      self: string
      first_name?: string | null
      last_name?: string | null
    }
    return {
      self,
      firstName: first_name ?? '',
      lastName: last_name ?? '',
    }
  }

  throw new Error('Unexpected editor shape in guest request response')
}

export function inputToAPIGuestRequestJSON(gr: GuestRequest): any {
  // Use normalizeEditors to extract self URLs from user objects
  const editors = normalizeEditors(gr?.editors)

  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map(editor => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  return {
    ...(gr.name && { name: gr.name }),
    ...(gr.email && { email: gr.email }),
    ...(gr.company && { company: gr.company }),
    ...(gr.phone && { phone: gr.phone }),
    ...(gr.quantity && { quantity: gr.quantity }),
    ...(gr.material && { material: gr.material }),
    ...(gr.message && { message: gr.message }),
    ...(gr.status && { status: gr.status }),
    ...(gr.remarks !== undefined && { remarks: gr.remarks }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
  }
}

/* -------------------------------------------------------------------------- */
/*                        Legacy API wrapper functions                        */
/* -------------------------------------------------------------------------- */

type FetchGuestRequestsArgs = {
  onSuccess: (guestRequests: GuestRequest[], count: number, next?: string | null) => void
  onFinal?: () => void
  onError?: (error: unknown) => void
  options?: PaginationOptions
}

export async function fetchGuestRequests({
  onSuccess,
  onFinal = () => undefined,
  onError = () => undefined,
  options = {},
}: FetchGuestRequestsArgs): Promise<void> {
  try {
    // Use deprecatedFetchGuestRequestsApi since it accepts PaginationOptions
    const response = await deprecatedFetchGuestRequestsApi(options)
    onSuccess(
      response.results,
      typeof response.count === 'number' ? response.count : response.results.length,
      response.next ?? null
    )
    onFinal()
  } catch (error) {
    onError(error)
  }
}

export async function fetchGuestRequest(
  idOrUrl: string | number,
  onSuccess: (guestRequest: GuestRequest) => void,
  onError: (error: unknown) => void = () => undefined,
  expand: string[] = ['editors', 'files']
): Promise<void> {
  try {
    const guestRequest = await fetchGuestRequestApi(idOrUrl, expand)
    onSuccess(guestRequest)
  } catch (error) {
    onError(error)
  }
}

type FetchGuestRequestFilesArgs = {
  url: string
  onSuccess: (files: GuestRequestFilesReference) => void
  onFinal?: () => void
  onError?: (error: unknown) => void
}

export async function fetchGuestRequestFiles({
  url,
  onSuccess,
  onFinal = () => undefined,
  onError = () => undefined,
}: FetchGuestRequestFilesArgs): Promise<void> {
  try {
    const normalizedUrl = url.endsWith('/') ? url : `${url}/`
    const response = await apiClient.get<PaginatedResponse<any>>(`${normalizedUrl}files/`)
    const files =
      Array.isArray(response.results) && response.results.length > 0
        ? response.results.map(enrichFile)
        : []

    onSuccess(files)
    onFinal()
  } catch (error) {
    onError(error)
  }
}

export async function postGuestRequest(
  guestRequestInput: GuestRequest,
  _files: FileInfo[],
  onSuccess: (guestRequest: GuestRequest) => void,
  onError: (error: unknown) => void = () => undefined
): Promise<void> {
  try {
    const payload = inputToAPIGuestRequestJSON(guestRequestInput)
    const response = await apiClient.post<any>('v1/guest_request/requests/', payload)
    onSuccess(createGuestRequest(response))
  } catch (error) {
    onError(error)
  }
}

export async function updateGuestRequest(
  guestRequestInput: GuestRequest,
  onSuccess: (guestRequest: GuestRequest) => void,
  onError: (error: unknown) => void = () => undefined
): Promise<void> {
  if (!guestRequestInput) {
    onError(new Error('Guest request input is required'))
    return
  }

  const targetUrl =
    typeof guestRequestInput.self === 'string' && guestRequestInput.self.length > 0
      ? guestRequestInput.self
      : `guest_request/requests/${guestRequestInput.key}/`

  try {
    const payload = inputToAPIGuestRequestJSON(guestRequestInput)
    await apiClient.put(targetUrl, payload)

    const refreshed = await fetchGuestRequestApi(targetUrl, ['editors', 'files'])
    onSuccess(refreshed)
  } catch (error) {
    onError(error)
  }
}

export async function deleteGuestRequests(
  guestRequests: (string | number | GuestRequest)[],
  onSuccess: () => void,
  onError: (error: unknown) => void = () => undefined
): Promise<void> {
  try {
    // Extract IDs/self from GuestRequest objects if needed
    const idsOrSelfs: (string | number)[] = guestRequests.map(req => {
      if (typeof req === 'string' || typeof req === 'number') {
        return req
      }
      // It's a GuestRequest object - extract self (similar to deleteRequestsApi)
      return req.self
    })
    await deleteGuestRequestsApi(idsOrSelfs)
    onSuccess()
  } catch (error) {
    onError(error)
  }
}

type RequestCostBreakdown = Record<string, any>

function resolveRequestCostBreakdown(
  request: Request
): RequestCostBreakdown | undefined {
  const breakdown =
    request.calculatedCostBreakdown ??
    (request as { calculated_cost_breakdown?: unknown }).calculated_cost_breakdown

  if (!breakdown || typeof breakdown !== 'object') return undefined
  return breakdown as RequestCostBreakdown
}

function resolveRequestCalculatedCosts(
  request: Request
): Record<string, any> | undefined {
  const costs =
    request.calculatedCosts ??
    (request as { calculated_costs?: unknown }).calculated_costs

  if (!costs || typeof costs !== 'object') return undefined
  return costs as Record<string, any>
}

export function getRequestUnitCostBreakdown(
  request: Request
): RequestCostBreakdown | undefined {
  const breakdown = resolveRequestCostBreakdown(request)
  if (breakdown) {
    const quantityKey = String(request.quantities?.[0] ?? 1)
    return (
      breakdown['1'] ??
      breakdown[1] ??
      breakdown[quantityKey]
    )
  }

  return undefined
}

export function getRequestUnitEstimatedPrice(request: Request): string | undefined {
  const unitBreakdown = getRequestUnitCostBreakdown(request)
  const price = unitBreakdown?.estimated_price
  if (typeof price === 'string' && price !== "loading") return price

  const fallbackCosts =
    request.calculatedCosts ??
    (request as { calculated_costs?: unknown }).calculated_costs

  if (
    typeof fallbackCosts === 'object' &&
    fallbackCosts !== null &&
    'estimated_price' in fallbackCosts
  ) {
    const price = (fallbackCosts as { estimated_price: unknown }).estimated_price
    if (typeof price === 'string') return price
    if (typeof price === 'number') return price.toString()
  }

  if (typeof fallbackCosts === 'string') return fallbackCosts
  if (typeof fallbackCosts === 'number') return String(fallbackCosts)

  const costs = resolveRequestCalculatedCosts(request)
  if (!costs) return undefined

  const quantityKey = String(request.quantities?.[0] ?? 1)
  const unitPrice = costs['1'] ?? costs[1] ?? costs[quantityKey]
  return typeof unitPrice === 'string' ? unitPrice : undefined
}

/**
 * Mirrors backend quantity scaling for estimated_price:
 * factor(q) = 1 / (1 + log10(q)), rounded to 2 decimals (HALF_UP for positive values).
 */
export function calculateQuantityAdjustedEstimatedPrice(
  unitEstimatedPrice: number,
  quantity: number
): number {
  if (!Number.isFinite(unitEstimatedPrice)) return 0

  const safeQuantity = Math.max(Math.trunc(Number(quantity)), 1)
  const scaleFactor =
    safeQuantity > 1 ? 1 / (1 + Math.log10(safeQuantity)) : 1

  return Math.round(unitEstimatedPrice * scaleFactor * 100) / 100
}

export function isRequestUnitPriceReady(request: Request): boolean {
  const price = getRequestUnitEstimatedPrice(request)
  if (typeof price !== 'string') return false
  const normalized = price.trim().toLowerCase()
  return normalized !== '' && normalized !== 'loading'
}

/**
 * Helper function that parses an Article object into a productInfoFormState
 * object that matches the structure required by the PartInfoDrawer.
 * @param part The Article to parse.
 * @returns
 */
export function partToProductInfoFormState(
  part: Part,
  productInfoFormState: ProductInfoFormStateInput | undefined,
  tempRequestInstance: Request,
  displayName?: string
): ProductInfoFormStateInput {
  return {
    name: part.name ?? '',
    displayName,
    self: part.self ?? '',
    quantity: productInfoFormState?.quantity ?? tempRequestInstance.quantities[0] ?? 1,
    request: (() => {
      const expandedRequests =
        Array.isArray(part.requests) && part.requests.length > 0
          ? part.requests.filter(
            (candidate): candidate is Request =>
              typeof candidate === 'object' && candidate !== null
          )
          : []
      const partRequest = expandedRequests.find(r => r.article === part.self)
      const fallbackRequest =
        (part.request && typeof part.request !== 'string' ? part.request : undefined) ??
        tempRequestInstance
      return partRequest ?? fallbackRequest
    })(),
  }
}

/**
 * Helper function that parses an Article object into a partInfoFormState
 * object that matches the structure required by the PartInfoDrawer.
 * @param part The Article object to parse
 * @returns
 */
export function partToPartInfoFormState(
  part: Part,
  manufacturingProcessOptions: SelectOption[],
  tempRequestInstance: Request,
  displayName?: string
): PartInfoFormStateInput {
  const technicalDrawing =
    Array.isArray(part.files)
      ? part.files.find(file => {
          const fileName = file.name || file.key || file.document || ''
          return fileName.includes('__technical_drawing')
        }) ?? null
      : null

  const formState: PartInfoFormStateInput = {
    name: part.name ?? '',
    displayName,
    manufacturing_process: '',
    material: '',
    qualityClass: '',
    self: '',
    quantity: 0,
    treatments: [],
    files: typeof part.files !== "string" ? part.files : []
  }
  if (!Array.isArray(part?.properties)) {
    throw new Error(
      'To parse an Article to a partInfoFormState object the ' +
      'properties field must be available and expanded on the Article.'
    )
  }
  if (part.properties.length === 0) {
    throw new Error(
      'To parse an Article to a partInfoFormState object there ' +
      'has to at least one property (manufacturingProcess/treatment) available on the Article.'
    )
  }

  // parse article properties
  part.properties.forEach((prop: Property | string) => {
    // type check to make TypeScript happy
    if (typeof prop === 'string') return
    if (prop.name === 'treatment' && prop.value !== '') {
      if (!formState['treatments']) {
        formState['treatments'] = [prop.value]
      } else {
        formState['treatments'].push(prop.value)
      }
    } else if (prop.name === 'manufacturing_process') {
      // manufacturing processes are provided by their labels
      // convert the label to its value
      const process = manufacturingProcessOptions.find(p => p.label === prop.value)
      formState['manufacturing_process'] = process?.value ?? ''
    } else {
      return
    }
  })

  // Extract material from the material field (not from properties)
  // Material can be a code string (preferred), a hyperlink, or an expanded object with a name field.
  if (part.material) {
    if (typeof part.material === 'string') {
      const materialValue = part.material.trim()
      formState['material'] =
        materialValue !== '' && !materialValue.includes('/')
          ? materialValue.toLowerCase()
          : ''
    } else if (typeof part.material === 'object' && 'name' in part.material) {
      // Material is expanded as an object with a name field
      formState['material'] = String((part.material as { name: string }).name).toLowerCase()
    }
  }

  const expandedRequests =
  Array.isArray(part.requests) && part.requests.length > 0
    ? part.requests.filter(
      (candidate): candidate is Request =>
        typeof candidate === 'object' && candidate !== null
    )
    : []
  const partRequest = expandedRequests.find(r => r.article === part.self)
  const quantity = typeof partRequest?.quantities === "string" ? parseInt(partRequest.quantities) : (partRequest?.quantities[0] ?? 1)
  return {
    ...formState,
    qualityClass: part.qualityClass ?? '', // this is either undefined or a meaningful value
    self: part.self,
    quantity: quantity,
    // ensure treatments is always an array for the enhanced multi-select component
    treatments: formState['treatments'] || [],
    request: partRequest,
  }
}

/**
 * Helper function that takes the input of the CompleteRequestDrawer
 * and constructs an object from it that is accepted by the backend.
 * @param formInput
 */
export function formInputToAPICompleteRequest(
  formInput: CompleteRequestFormStateInput
): CompleteRequestData {
  const data: CompleteRequestData = {
    article: '',
    country: '',
    description: '',
    priority: false,
    wish_date: '',
    files: [],
    quantities: [],
  }
  // Pass through selected article hyperlink (request-scoped or items-scoped both supported by backend)
  if (typeof formInput.article === 'string' && formInput.article.trim() !== '') {
    data.article = formInput.article.trim()
  }
  if (
    formInput?.destinationCountry &&
    typeof formInput.destinationCountry === 'object' &&
    formInput.destinationCountry.value
  ) {
    data.country = formInput.destinationCountry.value
  }
  if (typeof formInput.description === 'string') {
    const desc = formInput.description.trim()
    if (desc !== '') {
      data.description = desc
    }
  }
  if (typeof formInput.customerPriceSuggestion === 'string') {
    const suggestion = formInput.customerPriceSuggestion.trim()
    if (suggestion !== '') {
      data.customer_price_suggestion = suggestion
    }
  }
  if (Array.isArray(formInput.quantities) && formInput.quantities.length > 0) {
    data.quantities = formInput.quantities
  }
  if (formInput.shippingPeriod === 'asap') {
    if (formInput.shippingPeriodAsap === 'urgent') {
      data.wish_date = undefined
      data.priority = true
    } else {
      data.wish_date = undefined
      data.priority = false
    }
  } else if (formInput.shippingPeriod === 'deadline') {
    if (formInput.deadline && formInput.deadline !== '') {
      data.wish_date = formInput.deadline
      data.priority = true
    }
  } else if (formInput.shippingPeriod === 'wishDate') {
    if (formInput.wishDate && formInput.wishDate !== '') {
      data.wish_date = formInput.wishDate
    }
  }
  // Pass through articlesWithQuantities for multi-article requests
  if (Array.isArray(formInput.articlesWithQuantities) && formInput.articlesWithQuantities.length > 0) {
    data.articles_with_quantities = formInput.articlesWithQuantities
  }
  return data
}

export function parsePriceString(priceString: string): { value: number; currency: string } {
  if (!priceString) {
    throw new Error('Received invalid price string: empty value.')
  }
  const trimmed = priceString.trim()
  if (trimmed === '' || trimmed.toLowerCase() === 'loading') {
    throw new Error('Received invalid price string: loading/empty value.')
  }

  const currency = trimmed.includes('€') ? '€' : trimmed.includes('$') ? '$' : '€'
  const numericMatch = trimmed.match(/-?[\d.,]+/)
  if (!numericMatch) {
    throw new Error('Invalid price string format ' + priceString)
  }

  const numericPart = numericMatch[0]
  const lastComma = numericPart.lastIndexOf(',')
  const lastDot = numericPart.lastIndexOf('.')

  let normalized = numericPart
  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastDot > lastComma ? '.' : ','
    const thousandSeparator = decimalSeparator === '.' ? ',' : '.'
    normalized = numericPart.split(thousandSeparator).join('')
    if (decimalSeparator === ',') normalized = normalized.replace(',', '.')
  } else if (lastComma !== -1) {
    const decimals = numericPart.length - lastComma - 1
    if (decimals === 2) {
      normalized = numericPart.replace(/\./g, '').replace(',', '.')
    } else {
      normalized = numericPart.replace(/,/g, '')
    }
  } else if (lastDot !== -1) {
    const decimals = numericPart.length - lastDot - 1
    if (decimals === 2) {
      normalized = numericPart.replace(/,/g, '')
    } else {
      normalized = numericPart.replace(/\./g, '')
    }
  } else {
    normalized = numericPart.replace(/[,.]/g, '')
  }

  const value = Number.parseFloat(normalized)
  if (!Number.isFinite(value)) {
    throw new Error('Invalid price string format ' + priceString)
  }
  return { value, currency }
}

/**
 * Format a numeric price to a human-friendly string using locale rules.
 * Defaults to German formatting (e.g. 12700000 -> "12.700.000,00").
 */
export function formatPrice(value: number, locale = 'de-DE'): string {
  try {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return '0,00'
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue)
  } catch {
    return '0,00'
  }
}
